/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Cross-platform bridge that mirrors the widget snapshot into OS-shared storage
 * so the native home-screen widgets (which cannot read AsyncStorage/SecureStore)
 * can render it.
 *
 *   - iOS   → App Group `UserDefaults` via `@bacons/apple-targets` `ExtensionStorage`,
 *             then asks WidgetKit to reload its timelines.
 *   - Android → a well-known AsyncStorage key the widget's headless task reads,
 *             then requests a RemoteViews re-render via `react-native-android-widget`.
 *
 * Every native dependency is required lazily inside try/catch: on a build that
 * does not ship the widget targets (or in Expo Go) the bridge degrades to a
 * silent no-op instead of throwing, so the app keeps running unchanged.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import {
    WIDGET_SNAPSHOT_KEY,
    WIDGET_TOKEN_KEY,
    WIDGET_INSIGHTS_KEY,
    WIDGET_SNAPSHOT_VERSION,
    WIDGET_NAMES,
    getWidgetAppGroup,
} from '../../config/widgetConfig';
import { WIDGET_REFRESH_MINUTES_KEY, normalizeRefreshMinutes } from '../../config/widgetRefreshConfig';
import { getAccessToken } from '../storage';
import { readAutoRefreshMinutes } from './widgetRefreshPrefs';

/**
 * Lazily construct the iOS App Group storage handle. Returns `null` when the
 * native module is unavailable (non-widget build / Expo Go / Android).
 * @returns {{ storage: object|null, reloadWidget: (name?: string) => void }}
 */
const getIosExtensionStorage = () => {
    try {
        // eslint-disable-next-line global-require
        const { ExtensionStorage } = require('@bacons/apple-targets');
        const storage = new ExtensionStorage(getWidgetAppGroup());
        const reloadWidget = (name) => {
            try {
                ExtensionStorage.reloadWidget(name);
            } catch (_error) {
                // WidgetKit not present — ignore.
            }
        };

        return { storage, reloadWidget };
    } catch (_error) {
        return { storage: null, reloadWidget: () => {} };
    }
};

/**
 * Lazily resolve the Android widget-update requester. Returns `null` when the
 * native module is unavailable.
 * @returns {((options: object) => Promise<void>)|null}
 */
const getAndroidRequestWidgetUpdate = () => {
    try {
        // eslint-disable-next-line global-require
        return require('react-native-android-widget').requestWidgetUpdate;
    } catch (_error) {
        return null;
    }
};

/**
 * Resolve the Happy widget palette honouring the user's persisted light/dark
 * choice (the same resolution the headless task handler uses), degrading to the
 * synchronous OS-appearance variant when the stored preference cannot be read.
 * Returns `undefined` when the module is unavailable, so `renderWidgetElement`
 * falls back to its own sync default.
 * @returns {Promise<{ isDark: boolean, colors: object }|undefined>}
 */
const resolveWidgetThemeSafely = async () => {
    try {
        // eslint-disable-next-line global-require
        const { resolveWidgetTheme, resolveWidgetThemeSync } = require('../../widgets/widgetTheme');

        try {
            return await resolveWidgetTheme();
        } catch (_error) {
            return resolveWidgetThemeSync();
        }
    } catch (_error) {
        return undefined;
    }
};

/**
 * Persist the compact widget snapshot into OS-shared storage and nudge the
 * native widgets to re-render. Safe to call unconditionally — no-ops when widget
 * targets are absent. Never throws.
 *
 * @param {object} snapshot Serializable snapshot (see `buildWidgetSnapshot.js`).
 * @returns {Promise<void>}
 */
export const writeWidgetSnapshot = async (snapshot) => {
    // `schemaVersion` lets the native extension skip a layout it does not know instead of crashing on it.
    const stamped = Number.isFinite(WIDGET_SNAPSHOT_VERSION)
        ? { ...(snapshot || {}), schemaVersion: WIDGET_SNAPSHOT_VERSION }
        : snapshot || {};
    const json = JSON.stringify(stamped);

    try {
        // Android reads the snapshot from AsyncStorage inside its headless task;
        // iOS keeps a copy too so a JS refresh path can inspect it.
        await AsyncStorage.setItem(WIDGET_SNAPSHOT_KEY, json);
    } catch (_error) {
        // Storage full / unavailable — the last snapshot stays.
    }

    if (Platform.OS === 'ios') {
        const { storage, reloadWidget } = getIosExtensionStorage();
        if (storage) {
            const refreshMinutes = await readAutoRefreshMinutes();
            try {
                // The big `insights` block goes to its own key: the shared snapshot stays small enough for every widget
                // timeline to parse inside the extension's memory budget (see `WIDGET_INSIGHTS_KEY`).
                const { insights, ...coreSnapshot } = stamped;
                storage.set(WIDGET_SNAPSHOT_KEY, insights ? JSON.stringify(coreSnapshot) : json);
                if (insights) {
                    storage.set(WIDGET_INSIGHTS_KEY, JSON.stringify(insights));
                }
                // The extension reads the user's auto-reload choice to pick its timeline reload policy.
                storage.set(WIDGET_REFRESH_MINUTES_KEY, refreshMinutes);
            } catch (_error) {
                // App Group not entitled on this build — ignore.
            }
            reloadWidget();
        }

        return;
    }

    if (Platform.OS === 'android') {
        const requestWidgetUpdate = getAndroidRequestWidgetUpdate();
        if (!requestWidgetUpdate) {
            return;
        }

        // Re-render each registered widget from the freshly written snapshot.
        // eslint-disable-next-line global-require
        const { renderWidgetElement } = require('../../widgets/renderWidget');
        const theme = await resolveWidgetThemeSafely();
        // Per-instance insight configs, loaded once so the (sync) per-instance
        // renderer below can pick its own config by `widgetId`.
        const instanceById = {};
        try {
            // eslint-disable-next-line global-require
            const { listInstanceConfigs } = require('./widgetInstanceConfig');
            (await listInstanceConfigs()).forEach((entry) => {
                instanceById[entry.widgetId] = entry.config;
            });
        } catch (_error) {
            // No instance configs — preset defaults apply.
        }
        await Promise.all(
            WIDGET_NAMES.map((widgetName) =>
                requestWidgetUpdate({
                    widgetName,
                    // `requestWidgetUpdate` calls this ONCE PER PLACED INSTANCE and
                    // hands it that instance's `WidgetInfo`
                    // ({ widgetName, widgetId, height, width } — see
                    // `react-native-android-widget/src/api/request-widget-update.tsx`,
                    // `widgetsInfo.forEach(async (info) => renderWidget(info))`).
                    // Dropping the argument made `tierForWidget(undefined)` resolve
                    // to `medium` on every app-driven republish, so a 2×1 cell got
                    // the full medium card clipped into it until the launcher's own
                    // 30-minute tick re-rendered it at the real size.
                    renderWidget: (widgetInfo) =>
                        renderWidgetElement(widgetName, snapshot, {
                            theme,
                            widgetInfo,
                            instanceConfig: instanceById[String(widgetInfo?.widgetId)],
                        }),
                    widgetNotFound: () => {},
                }).catch(() => {})
            )
        );
    }
};

/**
 * Ask WidgetKit to rebuild every iOS widget timeline now (`WidgetCenter.reloadAllTimelines`). The extension then
 * serves the App Group snapshot and pulls `/widgets/summary` itself. Best-effort, never throws; no-op on Android and
 * on builds without the widget target.
 * @returns {void}
 */
export const reloadWidgetTimelines = () => {
    if (Platform.OS !== 'ios') {
        return;
    }

    try {
        getIosExtensionStorage().reloadWidget();
    } catch (_error) {
        // WidgetKit not present — ignore.
    }
};

/**
 * Mirror the user's auto-reload choice into the App Group (iOS) and reload the timelines so the new cadence applies at
 * once. No-op on Android (its cadence is the provider `updatePeriodMillis` + the background task) and on builds without
 * the widget target. Never throws.
 * @param {number} minutes Requested minutes (normalised to a supported value).
 * @returns {void}
 */
export const syncWidgetRefreshInterval = (minutes) => {
    if (Platform.OS !== 'ios') {
        return;
    }

    try {
        const { storage, reloadWidget } = getIosExtensionStorage();
        if (storage) {
            storage.set(WIDGET_REFRESH_MINUTES_KEY, normalizeRefreshMinutes(minutes));
            reloadWidget();
        }
    } catch (_error) {
        // App Group unavailable - the extension keeps its 15 minute default.
    }
};

/**
 * Mirror the current auth token into the iOS App Group. The WidgetKit extension pulls `/widgets/summary` with it
 * (so a widget fills in even when the app is not running) and treats an empty token as signed out. A token change is
 * followed by a timeline reload so a widget added or left on the "sign in" card before login repaints at once.
 * Best-effort and never throws. No-op on Android (widgets there run JS and read the token from AsyncStorage).
 * @param {{ reload?: boolean }} [options] `reload: false` skips the timeline reload (the caller publishes right after).
 * @returns {Promise<void>}
 */
export const syncWidgetAuthToken = async ({ reload = true } = {}) => {
    if (Platform.OS !== 'ios') {
        return;
    }

    try {
        const token = await getAccessToken();
        const { storage } = getIosExtensionStorage();
        if (storage) {
            storage.set(WIDGET_TOKEN_KEY, token || '');
            if (reload) {
                reloadWidgetTimelines();
            }
        }
    } catch (_error) {
        // ignore
    }
};

/**
 * Replace the published snapshot with a SIGNED-OUT stub on logout and repaint every widget: the previous user's data (and
 * any clock that was running) must not stay on the home screen, and the widgets must say "sign in" instead of looking
 * dead. The stub keeps only the localized widget captions (no `generatedAt`, which is what marks "nothing published").
 * Also drops the App Group auth token on iOS. Best-effort, never throws.
 * @returns {Promise<void>}
 */
export const clearWidgetData = async () => {
    let stub = { signedOut: true };
    try {
        // Lazy: this module is loaded in the headless widget task, which must not pay for the snapshot builder up front.
        // eslint-disable-next-line global-require
        const { buildCommonWidgetLabels } = require('./buildWidgetSnapshot');

        stub = { signedOut: true, labels: { widgets: buildCommonWidgetLabels() } };
    } catch (_error) {
        // Captions fall back to the shipped Hungarian literals.
    }

    try {
        await writeWidgetSnapshot(stub);
    } catch (_error) {
        // ignore
    }

    if (Platform.OS === 'ios') {
        const { storage, reloadWidget } = getIosExtensionStorage();
        if (storage) {
            try {
                storage.remove(WIDGET_TOKEN_KEY);
                // The previous user's insight figures must not outlive the session either.
                storage.remove(WIDGET_INSIGHTS_KEY);
            } catch (_error) {
                // ignore
            }
            reloadWidget();
        }
    }
};
